import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Diamond, Grid3X3, Scale } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe } from '../components/TricolorStripe';
import { Newsletter } from '../components/Newsletter';
import { ClubCampaignBanner } from '../components/ClubCampaignBanner';
import { teams } from '../teamData';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2, suffix = '', plain = false }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const startValue = 0;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      setCount(Math.floor(progress * (end - startValue) + startValue));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{plain ? count : count.toLocaleString()}{suffix}</span>;
};

// Category images
const categoryImages = {
  soccer: '/products/category/soccer.jpg',
  basketball: 'https://images.pexels.com/photos/2346/sport-high-united-states-of-america-ball.jpg?w=800',
  referee: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800',
  training: '/products/category/training.jpg',
  accessories: 'https://images.unsplash.com/photo-1526232761682-d26e03ac148e?w=800',
};

// Hero montage slides - "Your Jersey, Your Colours" banner images
const HERO_SLIDES = Array.from({ length: 18 }, (_, i) => `/products/banner/${i + 1}.jpg`);

// Signature Pieces promo imagery (pink sheets removed; 5 curated jerseys)
const FEATURED_IMAGES = [
  '/products/featured/2.jpg',
  '/products/featured/3.jpg',
  '/products/featured/4.jpg',
  '/products/featured/6.jpg',
  '/products/featured/7.jpg',
];

// One-at-a-time auto-advancing signature pieces slideshow
const SignatureCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = FEATURED_IMAGES.length;

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIndex((p) => (p + 1) % count), 4500);
    return () => clearInterval(id);
  }, [paused, count]);

  const go = (dir) => setIndex((p) => (p + dir + count) % count);

  return (
    <div
      className="relative overflow-hidden bg-[#161616] border border-white/10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="signature-carousel"
    >
      <div className="relative w-full aspect-[3/2]">
        <AnimatePresence mode="wait">
          <motion.div
            key={FEATURED_IMAGES[index]}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0"
          >
            <Link to="/catalogue" data-testid={`signature-piece-${index}`} className="block w-full h-full">
              <img
                src={FEATURED_IMAGES[index]}
                alt={`Signature piece ${index + 1}`}
                className="w-full h-full object-contain"
              />
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>

      <button
        onClick={() => go(-1)}
        aria-label="Previous"
        data-testid="signature-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-[#CE2B37] text-white transition-colors duration-200"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => go(1)}
        aria-label="Next"
        data-testid="signature-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-[#CE2B37] text-white transition-colors duration-200"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {FEATURED_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`signature-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-[#CE2B37]' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
          />
        ))}
      </div>
    </div>
  );
};

// Auto-rotating 3-up product showcase (any category)
const ROTATE_MS = 4200;
const RotatingShowcase = ({ pool, lang }) => {
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);
  const items = (pool || []).filter((x) => x && x.image);
  const len = items.length;
  useEffect(() => {
    if (len <= 3 || paused) return;
    const id = setInterval(() => setStart((s) => (s + 3) % len), ROTATE_MS);
    return () => clearInterval(id);
  }, [len, paused]);
  if (len === 0) return null;
  const copy = lang === 'fr'
    ? { overline: 'De la collection', title: 'En rotation', cta: 'Voir tout le catalogue' }
    : { overline: 'From the collection', title: 'In Rotation', cta: 'Browse the full catalogue' };
  const ease = [0.22, 1, 0.36, 1];
  return (
    <section
      className="py-16 md:py-24 bg-[#0F0F0F] overflow-hidden"
      data-testid="rotating-showcase-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="font-dm text-xs tracking-[6px] text-[#CE2B37] uppercase mb-3">{copy.overline}</p>
            <h2 className="font-bebas text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-none">{copy.title}</h2>
          </div>
          {/* sleek progress bar */}
          <div className="hidden md:block w-40 h-[2px] bg-white/10 overflow-hidden rounded-full">
            <motion.div
              key={paused ? 'paused' : start}
              className="h-full bg-[#CE2B37]"
              initial={{ width: '0%' }}
              animate={{ width: paused ? '100%' : '100%' }}
              transition={{ duration: paused ? 0.3 : ROTATE_MS / 1000, ease: 'linear' }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {[0, 1, 2].map((k) => {
            const item = items[(start + k) % len];
            return (
              <div key={k} className="relative aspect-[4/5]" data-testid={`rotating-showcase-col-${k}`}>
                <AnimatePresence>
                  <motion.div
                    key={item.slug}
                    className="absolute inset-0"
                    initial={{ opacity: 0, scale: 1.05, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, scale: 0.97, filter: 'blur(8px)' }}
                    transition={{ duration: 0.9, ease, delay: k * 0.12 }}
                  >
                    <Link
                      to={`/product/${item.slug}`}
                      className="group block w-full h-full rounded-xl overflow-hidden ring-1 ring-white/8 hover:ring-[#CE2B37]/60 transition-all duration-500 relative"
                      data-testid={`rotating-showcase-item-${k}`}
                    >
                      <div className="absolute inset-0 bg-white">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain p-5 transition-transform duration-[900ms] ease-out group-hover:scale-[1.07]"
                        />
                      </div>
                      {/* gradient + caption */}
                      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-5">
                        <p className="font-dm text-[10px] uppercase tracking-[3px] text-[#F5A3AB]">{item.tag}</p>
                        <div className="flex items-center justify-between mt-1">
                          <h3 className="font-bebas text-xl md:text-2xl text-white tracking-wide">{item.name}</h3>
                          <span className="translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 text-white text-lg">→</span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link to="/catalogue" className="inline-flex items-center gap-2 font-dm text-sm text-white/70 hover:text-[#CE2B37] transition-colors duration-300" data-testid="rotating-showcase-cta">
            {copy.cta} →
          </Link>
        </div>
      </div>
    </section>
  );
};


export default function HomePage() {
  const { t, p, lang } = useLanguage();
  const [slideIndex, setSlideIndex] = useState(() => Math.floor(Math.random() * HERO_SLIDES.length));

  useEffect(() => {
    const timer = setInterval(() => setSlideIndex((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(timer);
  }, []);

  const prevSlide = () => setSlideIndex((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const nextSlide = () => setSlideIndex((i) => (i + 1) % HERO_SLIDES.length);

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="home-page">
      {/* Montage Hero - Carousel */}
      <section className="relative pt-24" data-testid="hero-section">
        <div className="relative h-[58vh] min-h-[440px] md:h-[66vh] overflow-hidden bg-black">
          {HERO_SLIDES.map((src, i) => (
            <div
              key={src}
              className={`absolute inset-0 transition-opacity duration-700 ${i === slideIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              data-testid={`hero-slide-${i}`}
            >
              <img src={src} alt={`DM Sports banner ${i + 1}`} className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/20" />
            </div>
          ))}

          {/* Overlay content - bottom-left, Adidas style */}
          <div className="absolute inset-0 flex items-end pointer-events-none">
            <motion.div
              key={slideIndex}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="w-full max-w-7xl mx-auto px-6 md:px-12 pb-10 md:pb-14"
            >
              <p className="font-dm text-[10px] md:text-xs tracking-[2px] text-[#CE2B37] uppercase mb-2">
                {t.hero.overline}
              </p>
              <h1
                className="font-bebas text-3xl sm:text-5xl lg:text-6xl text-white leading-[0.92] tracking-tight max-w-2xl"
                data-testid="hero-montage-title"
              >
                {t.hero.montageTitle}
                <br />
                {t.hero.montageTitle2}
              </h1>
              <p className="font-dm text-xs md:text-sm text-white/75 mt-3 max-w-xl leading-relaxed">
                <span className="text-white font-semibold">{t.hero.title} {t.hero.titleLine2}.</span> {t.hero.subtitle}
              </p>
              <div className="pointer-events-auto flex flex-wrap items-center gap-3 mt-5">
                <Link
                  to="/catalogue"
                  className="bg-white text-[#0D0D0D] font-bebas text-sm tracking-wider px-7 py-2.5 hover:bg-[#CE2B37] hover:text-white transition-colors duration-200"
                  data-testid="hero-cta-catalogue"
                >
                  {t.hero.exploreCta}
                </Link>
                <Link
                  to="/contact"
                  className="border border-white/60 text-white font-bebas text-sm tracking-wider px-7 py-2.5 hover:bg-white hover:text-[#0D0D0D] transition-colors duration-200"
                  data-testid="hero-cta-quote"
                >
                  {t.hero.quoteCta}
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Arrows */}
          <button
            onClick={prevSlide}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="absolute left-3 md:left-6 top-6 md:top-1/2 md:-translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-black/40 hover:bg-[#CE2B37] border border-white/30 text-white backdrop-blur-sm transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            aria-label="Next slide"
            data-testid="hero-next"
            className="absolute right-3 md:right-6 top-6 md:top-1/2 md:-translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-black/40 hover:bg-[#CE2B37] border border-white/30 text-white backdrop-blur-sm transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 right-6 md:right-12 z-20 flex gap-1 md:gap-2" data-testid="hero-pagination">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlideIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === slideIndex ? 'true' : undefined}
                data-testid={`hero-dot-${i}`}
                className={`h-1.5 shrink-0 transition-[width,background-color] duration-300 ${i === slideIndex ? 'w-4 md:w-7 bg-[#CE2B37]' : 'w-1.5 md:w-3 bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
        </div>
      </section>


      <TricolorStripe />

      {/* Stats Section */}
      <section className="bg-[#0D0D0D] py-6 md:py-10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { ...t.stats.athletes, suffix: '+' },
              { ...t.stats.years, suffix: '+' },
              { ...t.stats.sports, suffix: '' },
              { ...t.stats.founded, suffix: '', plain: true },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className={`text-center ${i < 3 ? 'border-r border-[#CE2B37]/30' : ''} ${i === 1 || i === 3 ? 'lg:border-r' : ''} ${i === 1 ? 'border-r-0 lg:border-r' : ''}`}
              >
                <p className="font-bebas text-3xl md:text-5xl text-white tracking-tight">
                  <AnimatedCounter end={stat.number} suffix={stat.suffix} plain={stat.plain} />
                </p>
                <p className="font-dm text-[10px] md:text-xs text-white/60 mt-1 tracking-wider uppercase">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* The All New Model Stadio - feature banner */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.8 }}
        className="relative w-full overflow-hidden"
        data-testid="stadio-wallpaper"
      >
        <Link to="/product/stadio" className="block relative" data-testid="stadio-wallpaper-link">
          <img src="/products/wallpaper/stadio.jpg" alt="Model Stadio" className="w-full h-auto block" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/25">
            <h2 className="font-bebas text-white text-center leading-none tracking-[0.12em] text-4xl sm:text-6xl lg:text-7xl drop-shadow-[0_4px_24px_rgba(0,0,0,0.75)]">
              THE ALL NEW: MODEL STADIO
            </h2>
          </div>
        </Link>
      </motion.section>

      <TricolorStripe />

      <section className="py-2 bg-[#0D0D0D]" data-testid="stacked-tiles-section">
        <div className="max-w-7xl mx-auto px-2 md:px-4">
          {/* Soccer - Full Width Feature (top) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-2"
          >
            <Link
              to="/catalogue?sport=soccer"
              className="group relative block h-[220px] md:h-[340px] overflow-hidden"
              data-testid="category-tile-soccer"
            >
              <img
                src={categoryImages.soccer}
                alt="Soccer"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-[50%] group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4 md:bottom-8 md:left-8">
                <span className="font-dm text-[10px] tracking-[3px] text-[#CE2B37] uppercase mb-1 block">
                  {lang === 'en' ? 'Shop by Category' : 'Magasiner par catégorie'}
                </span>
                <h3 className="font-bebas text-3xl md:text-5xl text-white tracking-tight mb-2">SOCCER</h3>
                <span className="inline-block bg-white text-[#0D0D0D] font-bebas text-xs tracking-wider px-4 py-1.5 group-hover:bg-[#CE2B37] group-hover:text-white transition-colors duration-300">
                  {lang === 'en' ? 'Explore Soccer' : 'Explorer le soccer'}
                </span>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 bg-[#CE2B37] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white text-sm">→</span>
              </div>
            </Link>
          </motion.div>

          {/* Row - Training, Gear, Referee, Basketball */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { key: 'training', to: '/catalogue?sport=training', img: categoryImages.training, label: lang === 'en' ? 'TRAINING' : 'ENTRAÎNEMENT' },
              { key: 'accessories', to: '/catalogue?sport=accessories', img: categoryImages.accessories, label: lang === 'en' ? 'GEAR' : 'ÉQUIPEMENT' },
              { key: 'referee', to: '/catalogue?sport=referee', img: categoryImages.referee, label: lang === 'en' ? 'REFEREE' : 'ARBITRE' },
              { key: 'basketball', to: '/catalogue?sport=basketball', img: categoryImages.basketball, label: 'BASKETBALL' },
            ].map((cat, i) => (
              <motion.div
                key={cat.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
              >
                <Link
                  to={cat.to}
                  className="group relative block h-[140px] md:h-[200px] overflow-hidden"
                  data-testid={`category-tile-${cat.key}`}
                >
                  <img
                    src={cat.img}
                    alt={cat.label}
                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-[50%] group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4">
                    <h3 className="font-bebas text-lg md:text-2xl text-white tracking-tight">{cat.label}</h3>
                  </div>
                  <div className="absolute top-2 right-2 w-7 h-7 bg-[#CE2B37] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-white text-xs">→</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Custom Team Kits - its own small section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-2"
          >
            <Link
              to="/custom-uniforms"
              className="group relative block h-[150px] md:h-[200px] overflow-hidden"
              data-testid="category-tile-custom"
            >
              <img
                src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1600"
                alt="Custom Team Kits"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-[50%] group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-center pl-4 md:pl-10">
                <span className="font-dm text-[10px] tracking-[3px] text-[#CE2B37] uppercase mb-1">
                  {lang === 'en' ? 'New for 2026' : 'Nouveau pour 2026'}
                </span>
                <h3 className="font-bebas text-2xl md:text-4xl text-white tracking-tight mb-2">
                  {lang === 'en' ? 'CUSTOM TEAM KITS' : "KITS D'ÉQUIPE"}
                </h3>
                <span className="inline-block w-fit bg-white text-[#0D0D0D] font-bebas text-xs tracking-wider px-4 py-1.5 group-hover:bg-[#CE2B37] group-hover:text-white transition-colors duration-300">
                  {lang === 'en' ? 'Design Yours' : 'Créez le vôtre'}
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Only For Winners wallpaper */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8 }}
            className="relative w-full overflow-hidden mt-2"
            data-testid="winners-wallpaper"
          >
            <Link to="/catalogue" className="block relative" data-testid="winners-wallpaper-link">
              <img src="/products/wallpaper/winners.jpg" alt="Only For Winners" className="w-full h-auto block" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                <h2 className="font-bebas text-white text-center leading-none tracking-[0.14em] text-5xl sm:text-7xl lg:text-8xl drop-shadow-[0_4px_24px_rgba(0,0,0,0.75)]">
                  ONLY FOR WINNERS
                </h2>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      <TricolorStripe />


      <RotatingShowcase pool={p} lang={lang} />

      <TricolorStripe />

      {/* AS St-Léonard 2027 Collection */}
      <ClubCampaignBanner team={teams['st-leonard']} lang={lang} href="/clubs/st-leonard" />

      <TricolorStripe />

      {/* Featured Products Section */}
      <section className="py-14 md:py-20 bg-[#0D0D0D]" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <p className="font-dm text-xs tracking-[6px] text-[#CE2B37] uppercase mb-4">
              {t.featured.overline}
            </p>
            <h2 className="font-bebas text-3xl md:text-4xl lg:text-5xl text-white tracking-tight">
              {t.featured.title}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <SignatureCarousel />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-center mt-8"
          >
            <Link
              to="/catalogue"
              className="font-dm text-sm text-[#CE2B37] hover:underline"
              data-testid="view-catalogue-link"
            >
              {t.featured.viewAll} →
            </Link>
          </motion.div>
        </div>
      </section>

      <TricolorStripe />

      {/* Brand Identity Section */}
      <section className="py-14 md:py-20 bg-[#FAFAFA]" data-testid="brand-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-0">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:pr-16"
            >
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
                <span className="text-[#0D0D0D]">{t.brand.line1}</span>
                <br />
                <span className="text-[#CE2B37]">{t.brand.line2}</span>
                <br />
                <span className="text-[#0D0D0D]">{t.brand.line3}</span>
              </h2>
              <p className="font-dm text-sm text-[#6B6B6B] mt-5 leading-relaxed max-w-lg">
                {t.brand.body}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center font-dm text-sm text-[#CE2B37] mt-4 hover:underline"
                data-testid="brand-read-more"
              >
                {t.brand.cta} →
              </Link>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-[300px] lg:h-auto"
            >
              <img
                src="/products/santa-cruz/thumbnail.jpg"
                alt="Mass Sport athletes"
                className="absolute inset-0 w-full h-full object-cover object-top grayscale"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Why Choose Us Section */}
      <section className="py-14 md:py-20 bg-[#FAFAFA]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <p className="font-dm text-xs tracking-[6px] text-[#CE2B37] uppercase mb-4">
              {t.whyUs.overline}
            </p>
            <h2 className="font-bebas text-3xl md:text-4xl lg:text-5xl text-[#0D0D0D] tracking-tight">
              {t.whyUs.title}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {t.whyUs.features.map((feature, i) => {
              const icons = [Diamond, Grid3X3, Scale];
              const Icon = icons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-white border border-[#E8E8E8] p-8"
                >
                  <div className="w-12 h-12 bg-[#CE2B37]/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#CE2B37]" />
                  </div>
                  <h3 className="font-bebas text-2xl text-[#0D0D0D] tracking-wide mb-4">
                    {feature.title}
                  </h3>
                  <p className="font-dm text-sm text-[#6B6B6B] leading-relaxed">
                    {feature.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <TricolorStripe />


      {/* Berlino M040 wallpaper */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.8 }}
        className="relative w-full overflow-hidden"
        data-testid="berlino-wallpaper"
      >
        <img src="/products/wallpaper/berlino.jpg" alt="Berlino M040" className="w-full h-auto block" />
      </motion.section>

      {/* Custom Uniforms Teaser */}
      <section className="relative" data-testid="custom-teaser-section">
        <div className="bg-[#CE2B37] py-16 md:py-24 px-6 md:px-12 lg:px-14">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-bebas text-3xl md:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
              {t.customTeaser.title}
            </h2>
            <p className="font-dm text-sm text-white/85 mt-4 leading-relaxed">
              {t.customTeaser.body}
            </p>
            <Link
              to="/custom-uniforms"
              className="inline-block bg-white text-[#CE2B37] font-bebas text-base tracking-wider px-8 py-3 mt-6 hover:bg-[#FAFAFA] transition-colors duration-200"
              data-testid="custom-teaser-cta"
            >
              {t.customTeaser.cta}
            </Link>
          </motion.div>
        </div>
      </section>

      <TricolorStripe />

      {/* Testimonials Section */}
      <section className="py-14 md:py-20 bg-[#FAFAFA]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-3xl md:text-4xl text-[#0D0D0D] tracking-tight text-center mb-10"
          >
            {t.testimonials.title}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-5">
            {t.testimonials.items.map((item, i) => (
              <motion.div
                key={item.author}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white border border-[#E8E8E8] p-6"
              >
                <p className="font-dm text-sm text-[#6B6B6B] leading-relaxed italic">
                  "{item.quote}"
                </p>
                <p className="font-dm text-sm text-[#0D0D0D] mt-4 font-medium">
                  {item.author}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Macron Distributor Band */}
      <section className="py-16 md:py-24 bg-[#0D0D0D] border-t border-white/5" data-testid="macron-band">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16"
          >
            <a
              href="https://www.macron.com/wo/"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 group"
              data-testid="macron-band-logo"
            >
              <img
                src="/macron/macron-white.png"
                alt={t.macron.logoAlt}
                className="h-12 md:h-16 w-auto transition-transform duration-300 group-hover:scale-[1.04]"
              />
            </a>

            <div className="flex-1 text-center lg:text-left">
              <p className="font-dm text-xs uppercase tracking-[3px] text-[#CE2B37] mb-3">
                {t.macron.homeHeading}
              </p>
              <p className="font-dm text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
                {t.macron.homeBody}
              </p>
            </div>

            <Link
              to="/macron"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-[#0D0D0D] font-bebas text-lg tracking-wider px-8 py-4 hover:bg-white/85 transition-colors duration-200"
              data-testid="macron-band-cta"
            >
              {t.macron.viewCollection}
              <ChevronRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
